import { Search, X } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'

import { BottomSheet } from '@/components/ui/bottom-sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

import type { EpubSearchResult } from './continuous-epub-viewer'

type Props = {
  open: boolean
  onClose: () => void
  onSearch: (query: string) => Promise<EpubSearchResult[]>
  onNavigate: (result: EpubSearchResult) => void
}

export const SearchDialog = ({ open, onClose, onSearch, onNavigate }: Props) => {
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<EpubSearchResult[]>([])
  const [isSearching, setIsSearching] = useState(false)

  /** クライアントサイド検索 */
  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) {
      setSearchResults([])
      return
    }

    setIsSearching(true)

    try {
      const results = await onSearch(searchQuery.trim())
      setSearchResults(results)
    } catch (error) {
      console.error('検索に失敗しました:', error)
      setSearchResults([])
    } finally {
      setIsSearching(false)
    }
  }, [searchQuery, onSearch])

  const handleClearSearch = useCallback(() => {
    setSearchQuery('')
    setSearchResults([])
  }, [])

  const handleResultClick = useCallback(
    (result: EpubSearchResult) => {
      if (!result.locator) return
      onNavigate(result)
    },
    [onNavigate],
  )

  /** チャプターごとに結果をグループ化 */
  const groupedResults = useMemo(() => {
    const resultMap = new Map<string, EpubSearchResult[]>()
    for (const result of searchResults) {
      const key = result.chapterTitle || result.locator.href
      const existing = resultMap.get(key) ?? []
      resultMap.set(key, [...existing, result])
    }
    return resultMap
  }, [searchResults])

  return (
    <BottomSheet
      open={open}
      onOpenChange={(isOpen) => !isOpen && onClose()}
      title={searchResults.length > 0 ? `検索 (${searchResults.length}件)` : '検索'}
      snapPoints={[0.5, 1]}
      defaultSnapPoint={0.5}
      noOverlay
      dismissible={false}
    >
      <div className="flex flex-col h-full">
        {/* 検索入力エリア */}
        <div className="px-4 py-3 border-b">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Input
                type="text"
                placeholder="検索キーワードを入力..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleSearch()
                  }
                }}
                className="pr-8"
                autoFocus={open}
              />
              {searchQuery && (
                <button
                  onClick={handleClearSearch}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-muted-foreground hover:text-foreground"
                >
                  <X size={16} />
                </button>
              )}
            </div>
            <Button onClick={handleSearch} disabled={isSearching || !searchQuery.trim()}>
              <Search size={18} />
            </Button>
          </div>
        </div>

        {/* 検索結果一覧 */}
        <div className="flex-1 overflow-y-auto">
          {isSearching ? (
            <div className="flex items-center justify-center h-32 text-muted-foreground">検索中...</div>
          ) : searchResults.length > 0 ? (
            <div>
              {/* 結果件数 */}
              <div className="px-4 py-2 text-sm text-muted-foreground bg-muted border-b border-border">
                {searchResults.length}件の結果
              </div>

              {/* チャプターごとにグループ化された結果 */}
              {[...groupedResults.entries()].map(([groupKey, groupResults]) => (
                <div key={groupKey} className="relative">
                  {/* スティッキーチャプターヘッダー */}
                  <div className="sticky top-0 z-10 px-4 py-2 bg-secondary border-b border-border">
                    <span className="text-sm text-secondary-foreground font-medium truncate block">{groupKey}</span>
                    <span className="text-xs text-muted-foreground">({groupResults.length}件)</span>
                  </div>

                  {/* チャプター内の結果 */}
                  <div className="divide-y divide-border">
                    {groupResults.map((result, index) => (
                      <button
                        key={`${groupKey}-${index}`}
                        onClick={() => handleResultClick(result)}
                        className={cn(
                          'w-full text-left px-4 py-3 hover:bg-accent transition-colors',
                          'focus:outline-none focus:bg-accent',
                        )}
                      >
                        <p className="text-sm leading-relaxed text-foreground">
                          {result.locator.text?.before}
                          <mark className="bg-yellow-200 text-black px-0.5 rounded">
                            {result.locator.text?.highlight}
                          </mark>
                          {result.locator.text?.after}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : searchQuery && !isSearching ? (
            <div className="flex items-center justify-center h-32 text-muted-foreground">
              結果が見つかりませんでした
            </div>
          ) : (
            <div className="flex items-center justify-center h-32 text-muted-foreground">
              キーワードを入力して検索してください
            </div>
          )}
        </div>
      </div>
    </BottomSheet>
  )
}
